import UserAvatar from "@/components/ui/UserAvatar";
import { useEffect, useMemo, useRef, useState } from "react";
import { Send, AtSign, Pencil, Trash2, Check, X, Flame, AlertTriangle, MessageSquare } from "lucide-react";
import { toast } from "@/lib/toast";
import { notifyMessagesRead } from "@/lib/unreadMessages";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { avatarColorForSeed } from "@/lib/avatarColors";
import useAviStream from "@/hooks/useAviStream";
import ConfirmDestructiveDialog from "@/components/common/ConfirmDestructiveDialog";
// Spec iter132 pt.2 (Feb 2026) — capitalisation auto AVI.
import { withFirstLetterCapitalized } from "@/lib/autoCapitalize";

/**
 * AviChat — chat vertical avec @mentions et fenêtre d'édition 5 min.
 *
 * SSE : la mise à jour temps réel est déclenchée par les events
 * `avi.message` publiés par le backend — on ne repolle pas manuellement.
 */
const EDIT_WINDOW_MS = 5 * 60 * 1000;
const MENTION_RE = /@([\wÀ-ÿ.-]{1,40})$/;

function Avatar({ user, size = 30 }) {
  return <UserAvatar user={user} size={size} showPopover={false} />;
}

function renderText(text, usersById) {
  // Souligne les @mentions résolvables ; conserve les retours à la ligne.
  const parts = (text || "").split(/(\s+)/);
  return parts.map((seg, i) => {
    if (seg.startsWith("@")) {
      const tag = seg.slice(1).replace(/[^\wÀ-ÿ.-]/g, "").toLowerCase();
      const user = Object.values(usersById).find((u) => {
        const first = (u.first_name || "").toLowerCase();
        const last = (u.last_name || "").toLowerCase();
        return tag === first || tag === last || tag === `${first}.${last}`;
      });
      if (user) {
        return (
          <span key={i} className="font-semibold px-1 rounded"
                style={{ color: "#F472B6", background: "rgba(236,72,153,0.12)" }}>
            @{`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email}
          </span>
        );
      }
    }
    return <span key={i}>{seg}</span>;
  });
}

export default function AviChat({ aviId, participants = [], usersById = {} }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {id, text}
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  // Spec iter132 pt.3 (Feb 2026) — toggle « Urgent » du composer AVI.
  // Éphémère : se remet à false après chaque envoi (pas de persistance),
  // exactement comme le composer Tâches.
  const [urgent, setUrgent] = useState(false);
  // Spec iter212 (2026-02-28) — remplace `confirm("Supprimer ce message ?")`
  // par ConfirmDestructiveDialog thémé.
  const [msgToDelete, setMsgToDelete] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Ensemble des utilisateurs mentionables : demandeur + participants.
  const mentionCandidates = useMemo(() => {
    const ids = new Set(participants.map((p) => p.user_id));
    return Array.from(ids).map((id) => usersById[id]).filter(Boolean);
  }, [participants, usersById]);

  const highlightMessageId = new URLSearchParams(window.location.search).get("msg");
  const highlightedRef = useRef(false);

  const load = async () => {
    try {
      const r = await api.get(`/avi/${aviId}/messages`);
      setMessages(r.data.items || []);
      notifyMessagesRead();
    } catch (e) { /* silent */ }
  };

  useEffect(() => { if (aviId) load(); }, [aviId]); // eslint-disable-line react-hooks/exhaustive-deps

  useAviStream({
    onAny: (evt) => {
      if (evt?.avi_id === aviId && evt?.kind === "message") load();
    },
  });

  useEffect(() => {
    if (!scrollRef.current) return;
    if (highlightMessageId && !highlightedRef.current) {
      const el = scrollRef.current.querySelector(`[data-testid="avi-msg-${highlightMessageId}"]`);
      if (el) {
        highlightedRef.current = true;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("chat-highlight-pulse");
        setTimeout(() => el.classList.remove("chat-highlight-pulse"), 1500);
        return;
      }
    }
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, highlightMessageId]);

  const handleChange = (e) => {
    // Spec iter132 pt.2 — capitalisation auto de la 1ère lettre.
    if (typeof e?.target?.value === "string" && e.target.value.length > 0) {
      const first = e.target.value.charAt(0);
      const cap = first.toLocaleUpperCase("fr-FR");
      if (first !== cap) e.target.value = cap + e.target.value.slice(1);
    }
    const v = e.target.value;
    setText(v);
    // Détection du @mention en fin de saisie.
    const m = v.slice(0, e.target.selectionStart).match(MENTION_RE);
    if (m) {
      setMentionOpen(true);
      setMentionQuery(m[1].toLowerCase());
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (u) => {
    const el = inputRef.current;
    if (!el) return;
    const caret = el.selectionStart;
    const before = text.slice(0, caret).replace(MENTION_RE, "");
    const after = text.slice(caret);
    const handle = `@${(u.first_name || "").replace(/\s+/g, "")}.${(u.last_name || "").replace(/\s+/g, "")} `.toLowerCase();
    const nv = before + handle + after;
    setText(nv);
    setMentionOpen(false);
    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = before.length + handle.length;
    }, 0);
  };

  const extractMentions = (v) => {
    const ids = new Set();
    (v.match(/@([\wÀ-ÿ.-]+)/g) || []).forEach((tok) => {
      const tag = tok.slice(1).toLowerCase();
      const user = mentionCandidates.find((u) => {
        const first = (u.first_name || "").toLowerCase();
        const last = (u.last_name || "").toLowerCase();
        return tag === first || tag === last || tag === `${first}.${last}`;
      });
      if (user) ids.add(user.id);
    });
    return Array.from(ids);
  };

  const send = async () => {
    const t = text.trim();
    if (!t || saving) return;
    setSaving(true);
    // Spec iter132 pt.3 — capture le flag urgent AVANT l'envoi (immutabilité)
    // puis remise à false systématique sitôt le POST parti.
    const isUrgent = urgent;
    try {
      await api.post(`/avi/${aviId}/messages`, { text: t, mentions: extractMentions(t), urgent: isUrgent });
      setText("");
      setUrgent(false);
      if (isUrgent) {
        toast.success("Message urgent envoyé — email en cours d'expédition aux participants");
      }
      // Le SSE va recharger, mais on rafraîchit sans attendre pour l'auteur.
      await load();
    } catch (e) {
      toast.error("Échec de l'envoi", { description: e?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (m) => setEditing({ id: m.id, text: m.text });
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    if (!editing) return;
    const t = editing.text.trim();
    if (!t) return;
    try {
      await api.patch(`/avi/${aviId}/messages/${editing.id}`, { text: t, mentions: extractMentions(t) });
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "" });
    }
  };

  const remove = async (m) => {
    // Spec iter212 — ouverture du dialogue de confirmation stylé.
    setMsgToDelete(m);
  };

  const performRemove = async () => {
    if (!msgToDelete) return;
    try {
      await api.delete(`/avi/${aviId}/messages/${msgToDelete.id}`);
      await load();
      setMsgToDelete(null);
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "" });
    }
  };

  const filteredMentions = mentionQuery
    ? mentionCandidates.filter((u) => {
        const s = `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""}`.toLowerCase();
        return s.includes(mentionQuery);
      }).slice(0, 6)
    : mentionCandidates.slice(0, 6);

  const isAdmin = user?.role === "premier_admin" || user?.role === "admin";

  return (
    <div className="rounded-xl overflow-hidden flex flex-col"
         style={{ background: "#161616", border: "1px solid #2A2A2A", minHeight: 340 }}
         data-testid="avi-chat">
      <div className="px-4 py-3 flex items-center gap-2"
           style={{ borderBottom: "1px solid #2A2A2A" }}>
        <AtSign size={14} style={{ color: "#EC4899" }} />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          Discussion · {messages.length}
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 420 }}>
        {messages.length === 0 && (
          <div className="text-center text-xs text-muted-foreground py-6" data-testid="avi-chat-empty">
            Aucun message pour l'instant.
          </div>
        )}
        {messages.map((m) => {
          // Spec iter208bis (2026-02-28) — Messages système auto-postés
          // à la clôture d'une demande AVI (approuvée/refusée/reçue/lue).
          // Rendu distinct : centré, teinte couleur module, icône ✓, nom
          // + heure. Immutable (aucune action edit/delete disponible).
          if (m.is_system) {
            const u = usersById[m.user_id];
            const color = m.system_color || "#8B5CF6";
            const actor = u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : "—";
            const at = m.created_at
              ? new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })
              : "";
            return (
              <div
                key={m.id}
                className="flex justify-center my-2"
                data-testid={`avi-msg-sys-${m.id}`}
              >
                <div
                  className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11.5px]"
                  style={{
                    background: `${color}18`,
                    border: `1px solid ${color}55`,
                    color: "#EDEDED",
                  }}
                >
                  <Check size={12} strokeWidth={2.6} style={{ color }} />
                  <span className="font-semibold" style={{ color }}>{m.text}</span>
                  <span className="opacity-70">par</span>
                  <span className="font-medium">{actor}</span>
                  <span className="opacity-50">·</span>
                  <span className="tabular-nums opacity-70">{at}</span>
                </div>
              </div>
            );
          }
          const u = usersById[m.user_id];
          const mine = m.user_id === user?.id;
          const canEdit = mine && (Date.now() - new Date(m.created_at).getTime()) < EDIT_WINDOW_MS;
          const canDelete = canEdit || isAdmin;
          const isEditing = editing?.id === m.id;
          return (
            <div key={m.id} className="flex items-start gap-2 group" data-testid={`avi-msg-${m.id}`}>
              <Avatar user={u} size={30} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[12px] font-semibold">
                    {u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : (mine ? "Moi" : "—")}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(m.created_at).toLocaleString("fr-FR")}
                    {m.edited && <span className="ml-1 italic">(modifié)</span>}
                  </span>
                </div>
                {isEditing ? (
                  <div className="flex items-start gap-1">
                    <textarea
                      value={editing.text}
                      onChange={withFirstLetterCapitalized((e) => setEditing((v) => ({ ...v, text: e.target.value })))}
                      rows={2}
                      className="flex-1 p-2 rounded text-[13px] resize-none"
                      style={{ background: "#212121", border: "1px solid #383838", color: "#EDEDED" }}
                      autoFocus
                    />
                    <button type="button" onClick={saveEdit}
                            className="p-1.5 rounded" style={{ background: "#22C55E22", color: "#4ADE80" }}
                            data-testid={`avi-msg-save-${m.id}`}><Check size={13} /></button>
                    <button type="button" onClick={cancelEdit}
                            className="p-1.5 rounded" style={{ background: "#38383822", color: "#9CA3AF" }}
                            data-testid={`avi-msg-cancel-${m.id}`}><X size={13} /></button>
                  </div>
                ) : (
                  <div className="text-[13px] whitespace-pre-wrap leading-relaxed" style={{ color: "#D1D5DB" }}>
                    {renderText(m.text, usersById)}
                  </div>
                )}
              </div>
              {!isEditing && (canEdit || canDelete) && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canEdit && (
                    <button type="button" onClick={() => startEdit(m)}
                            className="p-1 rounded hover:bg-white/10 text-muted-foreground hover:text-white"
                            title="Modifier (5 min)"
                            data-testid={`avi-msg-edit-${m.id}`}>
                      <Pencil size={12} />
                    </button>
                  )}
                  {canDelete && (
                    <button type="button" onClick={() => remove(m)}
                            className="p-1 rounded hover:bg-white/10 text-muted-foreground hover:text-red-400"
                            title="Supprimer"
                            data-testid={`avi-msg-delete-${m.id}`}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Spec iter132 pt.3 (Feb 2026) — bandeau d'avertissement au-dessus
          du composer quand le toggle Urgent est actif. */}
      {urgent && (
        <div
          className="px-4 py-2 flex items-center gap-2"
          style={{ background: "rgba(224,122,122,0.10)", color: "#FCA5A5", borderTop: "1px solid #E07A7A" }}
          data-testid="avi-chat-urgent-warning"
        >
          <AlertTriangle size={13} strokeWidth={2.4} />
          <span className="text-[11.5px] font-medium">
            Ce message sera envoyé par <strong>email</strong> aux participants (demandeur + décideurs + témoins).
          </span>
        </div>
      )}
      <div className="p-3 relative" style={{ borderTop: urgent ? "none" : "1px solid #2A2A2A" }}>
        {mentionOpen && filteredMentions.length > 0 && (
          <div className="absolute left-3 bottom-full mb-1 rounded-md overflow-hidden z-10"
               style={{ background: "#212121", border: "1px solid #383838", minWidth: 220 }}
               data-testid="avi-chat-mentions">
            {filteredMentions.map((u) => (
              <button key={u.id} type="button" onClick={() => insertMention(u)}
                      className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-white/5 flex items-center gap-2">
                <Avatar user={u} size={22} />
                {`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          {/* Spec iter132 pt.3 (Feb 2026) — toggle « Urgent » en pilule.
              Position : juste avant le Textarea. État actif = fond rouge
              sombre, bordure rouge, texte rouge en 700. */}
          <button
            type="button"
            onClick={() => setUrgent((v) => !v)}
            aria-pressed={urgent}
            className="inline-flex items-center gap-1.5 shrink-0 transition-all"
            style={{
              height: 42,
              padding: "0 12px",
              borderRadius: 21,
              background: urgent ? "#3A1414" : "#242424",
              border: `1px solid ${urgent ? "#E07A7A" : "#3A3A3A"}`,
              color: urgent ? "#FCA5A5" : "#9CA3AF",
              fontSize: 12,
              fontWeight: urgent ? 700 : 500,
              letterSpacing: urgent ? "0.02em" : 0,
              cursor: "pointer",
            }}
            title={urgent ? "Urgent activé — cliquez pour annuler" : "Marquer ce message comme urgent (déclenche un email aux participants)"}
            data-testid="avi-chat-urgent-toggle"
          >
            <Flame size={14} strokeWidth={2.4} />
            Urgent
          </button>
          <textarea
            ref={inputRef}
            value={text}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !mentionOpen) {
                e.preventDefault(); send();
              }
              if (e.key === "Escape") setMentionOpen(false);
            }}
            placeholder="Message… (@prenom pour mentionner)"
            rows={2}
            className="flex-1 p-2.5 rounded-md text-[13px] resize-none placeholder:text-[#666]"
            style={{
              background: "#212121",
              border: `1px solid ${urgent ? "#E07A7A" : "#383838"}`,
              color: "#EDEDED",
              minHeight: 60,
              boxShadow: urgent ? "0 0 0 1px rgba(224,122,122,0.35)" : "none",
            }}
            data-testid="avi-chat-input"
          />
          <button type="button" onClick={send} disabled={!text.trim() || saving}
                  className="h-10 px-4 rounded-lg text-[12px] font-semibold text-white transition-all inline-flex items-center gap-1.5"
                  style={{
                    background: text.trim() && !saving ? (urgent ? "#C1401A" : "#EC4899") : "#2A2A2A",
                    cursor: text.trim() && !saving ? "pointer" : "not-allowed",
                    boxShadow: text.trim() && !saving
                      ? (urgent ? "0 4px 12px rgba(193,64,26,0.35)" : "0 4px 12px rgba(236,72,153,0.3)")
                      : "none",
                  }}
                  data-testid="avi-chat-send">
            <Send size={13} /> {saving ? "…" : "Envoyer"}
          </button>
        </div>
      </div>
      {/* Spec iter212 — Confirmation stylée pour suppression message. */}
      <ConfirmDestructiveDialog
        open={!!msgToDelete}
        onOpenChange={(v) => { if (!v) setMsgToDelete(null); }}
        title="Supprimer ce message ?"
        description="Cette action est irréversible."
        icon={<MessageSquare size={20} />}
        body={msgToDelete ? (
          <div
            className="rounded-md px-3 py-2.5 text-[12.5px]"
            style={{ background: "#161616", border: "1px solid rgba(255,255,255,0.06)", color: "#EDEDED" }}
            data-testid="avi-chat-msg-preview"
          >
            <span className="opacity-60">« </span>
            {(msgToDelete.text || "").slice(0, 140)}
            {(msgToDelete.text || "").length > 140 ? "…" : ""}
            <span className="opacity-60"> »</span>
          </div>
        ) : null}
        warningText="Le message ne sera plus visible pour personne dans la discussion."
        confirmLabel="Supprimer"
        onConfirm={performRemove}
        testId="confirm-delete-avi-msg"
      />
    </div>
  );
}
